import { DatePicker, Form, Modal, Radio, TreeSelect } from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  V2HighEnergyDeviceDeviceTreePostResponse,
  apiV2HighEnergyDeviceDeviceTreePost,
} from '@maxtropy/device-customer-apis-v2';
import { DateType, DateTypeDisplay, overallGetTime } from '../utils';
import qs from 'qs';
import useLatestRequest from '../useLatestRequest';

export type DeviceTreeData = Exclude<V2HighEnergyDeviceDeviceTreePostResponse['list'], undefined>[number];

interface ExportEnergyModalProps {
  visible: boolean;
  onClose: () => void;
  energyMediumId?: string;
  energyUnitId?: number;
  dataQueryPeriodId?: number;
}

interface MyTreeNode {
  key: string;
  title: string;
  value: string;
  children?: MyTreeNode[];
  [key: string]: any;
}

const ExportEnergyModal: React.FC<ExportEnergyModalProps> = ({
  visible,
  onClose,
  energyMediumId,
  energyUnitId,
  dataQueryPeriodId,
}) => {
  const [form] = Form.useForm();
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [displayValue, setDisplayValue] = useState<Dayjs>(dayjs());
  const [deviceOp, setDeviceOp] = useState<MyTreeNode[]>([]);
  const { runLatest } = useLatestRequest();
  const timeRangeTidy = useMemo(() => {
    return [
      overallGetTime(timeResolution, displayValue).startTime,
      overallGetTime(timeResolution, displayValue).endTime,
    ];
  }, [timeResolution, displayValue]);

  useEffect(() => {
    runLatest(() => apiV2HighEnergyDeviceDeviceTreePost({}), {
      onSuccess: res => {
        const treeData = transformTree(res.list);
        setDeviceOp(treeData);
      },
    });
  }, [runLatest]);

  const onRadioChange = (e: any) => {
    setTimeResolution(e.target.value as string as DateType);
    setDisplayValue(dayjs());
  };

  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(values => {
        const { inventoryIds } = values;
        const params = {
          energyMediumId,
          energyUnitId,
          dataQueryPeriodId,
          inventoryIds: inventoryIds,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          timeResolution: timeResolution,
        };
        window.open('/api/v2/high-energy/device/exportEnergyData' + qs.stringify(params, { addQueryPrefix: true }));
        // onClose();
      })
      .catch(() => {});
  };

  if (!visible) return null;

  return (
    <Modal
      title="导出用能数据"
      forceRender
      open
      afterClose={() => form.resetFields()}
      onCancel={onClose}
      contentClassName="modal-form-content"
      onOk={onFinish}
    >
      <div>
        <Form form={form}>
          <Form.Item label="导出时间维度" required>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
                <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
                <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
                <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
                <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
              </Radio.Group>
              <DatePicker
                picker={picker}
                style={{ flex: 1 }}
                value={displayValue}
                disabledDate={disabledDate}
                onChange={setDisplayValue}
                allowClear={false}
              />
            </div>
          </Form.Item>
          <Form.Item label="高能耗设备" name="inventoryIds" rules={[{ required: true, message: '请选择高能耗设备' }]}>
            <TreeSelect
              allowClear
              maxTagCount={3}
              placeholder="请选择"
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              treeCheckable={true}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeData={deviceOp}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default ExportEnergyModal;

const transformTree = (tree?: DeviceTreeData[]): MyTreeNode[] => {
  if (!tree) return [];
  return tree.map(item => {
    return {
      key: item.deviceTypeId?.toString(),
      title: item.deviceTypeName,
      value: item.deviceTypeId?.toString(),
      info: {
        name: item.deviceTypeName,
        id: item.deviceTypeId,
      },
      children: (item.deviceLedgerInfo ?? []).map(c => {
        return {
          key: c.id?.toString(),
          title: c.assetDeviceName,
          value: c.id?.toString(),
          info: {
            name: c.assetDeviceName,
            id: c.energyUnitId,
            deviceLedgerId: c.id,
          },
        };
      }),
    };
  }) as MyTreeNode[];
};
